import { useMemo, useRef } from 'react';

/**
 * A custom React hook that returns a frozen ref-like object whose `current` property
 * is always synchronized with the latest value passed to the hook.
 *
 * @template T The type of the value to be synchronized.
 * @param value - The value to be kept in sync with the returned ref's `current` property.
 * @returns A frozen object with a getter for `current` that always reflects the latest value.
 *
 * @example
 * ```tsx
 * const syncedRef = useSyncedRef(props.value);
 * // syncedRef.current will always be equal to props.value
 * ```
 */
export const useSyncedRef = <T>(value: T): { readonly current: T } => {
  const ref = useRef(value);

  ref.current = value;

  return useMemo(() => {
    return Object.freeze({
      get current() {
        return ref.current;
      },
    });
  }, []);
};
